import type { ReactElement, SVGProps } from 'react';

import { cn } from '../../lib/cn';

type TuileProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'children'>;

/**
 * Tuile 32×32 à fond plein, comme les icônes de marque de Stripe : le logo
 * occupe toute la vignette, l'arrondi et la taille viennent de la classe.
 */
function Tuile({ className, children, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('size-5 shrink-0 rounded-[3px] ring-1 ring-black/10', className)}
      {...props}
    >
      {children}
    </svg>
  );
}

/**
 * Repli pour les réseaux dont on n'a pas le tracé officiel : fond aux couleurs
 * de la marque, sigle court en blanc. Le corps de caractère suit la longueur du
 * sigle pour qu'il tienne dans les 32 unités sans déborder.
 */
function TuileSigle({
  fond,
  sigle,
  encre = '#fff',
  ...props
}: TuileProps & {
  fond: string;
  sigle: string;
  encre?: string;
}) {
  const corps = sigle.length <= 2 ? 13 : sigle.length === 3 ? 10 : 8;

  return (
    <Tuile {...props}>
      <path d="M0 0h32v32H0z" fill={fond} />
      <text
        x="16"
        y="16"
        textAnchor="middle"
        dominantBaseline="central"
        fill={encre}
        fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
        fontSize={corps}
        fontWeight="700"
        letterSpacing="-0.5"
      >
        {sigle}
      </text>
    </Tuile>
  );
}

export function CardVisa(props: TuileProps) {
  return (
    <Tuile {...props}>
      <path d="M0 0h32v32H0z" fill="#1434CB" />
      <g fill="#fff" fillRule="nonzero">
        <path d="M13.823 19.876H11.8l1.265-7.736h2.023zm7.334-7.546a5.036 5.036 0 0 0-1.814-.33c-1.998 0-3.405 1.053-3.414 2.56-.016 1.11 1.007 1.728 1.773 2.098.783.379 1.05.626 1.05.963-.009.518-.633.757-1.216.757-.808 0-1.24-.123-1.898-.411l-.267-.124-.283 1.737c.475.213 1.349.403 2.257.411 2.123 0 3.505-1.037 3.521-2.641.008-.881-.532-1.556-1.698-2.107-.708-.354-1.141-.593-1.141-.955.008-.33.366-.667 1.165-.667a3.471 3.471 0 0 1 1.507.297l.183.082zm2.69 4.806.807-2.165c-.008.017.167-.452.266-.74l.142.666s.383 1.852.466 2.239h-1.682zm2.497-4.996h-1.565c-.483 0-.85.14-1.058.642l-3.005 7.094h2.123l.425-1.16h2.597c.059.271.242 1.16.242 1.16h1.873zm-16.234 0-1.982 5.275-.216-1.07c-.366-1.234-1.515-2.575-2.797-3.242l1.815 6.765h2.14l3.18-7.728z" />
        <path d="M6.289 12.14H3.033L3 12.297c2.54.641 4.221 2.189 4.912 4.049l-.708-3.556c-.116-.494-.474-.633-.915-.65z" />
      </g>
    </Tuile>
  );
}

/**
 * Les deux disques de Mastercard : rouge à gauche, orange à droite, et la
 * lentille de recouvrement dessinée par-dessus. Deux arcs de rayon 7 suffisent,
 * les cercles se croisent en x = 16.
 */
export function CardMastercard(props: TuileProps) {
  return (
    <Tuile {...props}>
      <path d="M0 0h32v32H0z" fill="#fff" />
      <circle cx="12.5" cy="16" r="7" fill="#EB001B" />
      <circle cx="19.5" cy="16" r="7" fill="#F79E1B" />
      <path d="M16 9.939A7 7 0 0 1 16 22.061A7 7 0 0 1 16 9.939" fill="#FF5F00" />
    </Tuile>
  );
}

export function CardAmex(props: TuileProps) {
  return <TuileSigle fond="#006FCF" sigle="AMEX" {...props} />;
}

export function CardDiscover(props: TuileProps) {
  return <TuileSigle fond="#FF6000" sigle="DISC" {...props} />;
}

export function CardDiners(props: TuileProps) {
  return <TuileSigle fond="#0079BE" sigle="DC" {...props} />;
}

export function CardJcb(props: TuileProps) {
  return <TuileSigle fond="#0E4C96" sigle="JCB" {...props} />;
}

export function CardUnionPay(props: TuileProps) {
  return <TuileSigle fond="#E21836" sigle="UP" {...props} />;
}

export function CardCartesBancaires(props: TuileProps) {
  return <TuileSigle fond="#0B4EA2" sigle="CB" {...props} />;
}

export function CardLink(props: TuileProps) {
  return <TuileSigle fond="#00D66F" sigle="link" encre="#011E0F" {...props} />;
}

/** Réseau inconnu ou non reconnu par Stripe : vignette de carte neutre. */
export function CardInconnue(props: TuileProps) {
  return (
    <Tuile {...props}>
      <path d="M0 0h32v32H0z" fill="#6B7280" />
      <path d="M0 11h32v4H0z" fill="#fff" fillOpacity="0.9" />
      <path d="M5 20h9v3H5z" fill="#fff" fillOpacity="0.55" />
    </Tuile>
  );
}

/** Marques telles que Stripe les renvoie dans `card.brand`. */
const ICONES: Record<string, (props: TuileProps) => ReactElement> = {
  visa: CardVisa,
  mastercard: CardMastercard,
  amex: CardAmex,
  discover: CardDiscover,
  diners: CardDiners,
  jcb: CardJcb,
  unionpay: CardUnionPay,
  cartes_bancaires: CardCartesBancaires,
  link: CardLink,
};

const NOMS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  cartes_bancaires: 'Cartes Bancaires',
  link: 'Link',
};

export type CardBrandProps = TuileProps & {
  /** Valeur brute de `card.brand` côté Stripe. */
  marque: string;
};

/**
 * Logo du réseau d'une carte. Le nom de la marque part en `aria-label` : le
 * logo seul ne dit rien à un lecteur d'écran, et le libellé visible se réduit
 * aux quatre derniers chiffres.
 */
export function CardBrand({ marque, ...props }: CardBrandProps) {
  const Icone = ICONES[marque] ?? CardInconnue;
  return <Icone role="img" aria-label={NOMS[marque] ?? 'Carte bancaire'} {...props} />;
}
